import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Menu as MuiMenu,
  MenuItem,
  Paper,
  Autocomplete,
  Avatar,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tooltip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  Print,
  DragIndicator,
  SwapVert,
  CheckCircle,
  Visibility,
  MoreVert,
  Close,
  Delete,
  Add,
  PersonAdd,
  Person,
  Email,
  Phone,
  Business,
  ContentCopy,
} from '@mui/icons-material';
import { quotationAPI, customerAPI, productAPI, paymentTermAPI, userAPI, activityLogAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { ViewPageHeader, ActivityPanel, CustomerSelect, ProductSelect, PaymentTermSelect, ConfirmDialog } from '../../components/common';
import SendQuotationDialog from '../../components/common/SendQuotationDialog';

const initialLineItem = {
  product: '',
  productName: '',
  description: '',
  quantity: 1,
  unitPrice: 0,
  discount: 0,
  discountType: 'percentage',
  taxRate: 18,
};

const QuotationView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [status, setStatus] = useState('draft');
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  
  // Dropdown data
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [paymentTermsList, setPaymentTermsList] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  
  // Salesperson Dialog
  const [salespersonDialogOpen, setSalespersonDialogOpen] = useState(false);
  const [newSalesperson, setNewSalesperson] = useState({
    name: '',
    email: '',
    phone: '',
    department: '',
    role: 'sales',
  });
  
  // Record navigation state
  const [quotationIds, setQuotationIds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  
  // Preview dialog state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Duplicate loading state
  const [duplicating, setDuplicating] = useState(false);
  
  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Email send dialog
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailData, setEmailData] = useState({
    recipients: [],
    subject: '',
    message: '',
    attachPdf: true,
  });

  // Form data for inline editing
  const [formData, setFormData] = useState({
    quotationNumber: '',
    title: '',
    customer: null,
    quotationDate: dayjs(),
    validUntil: dayjs().add(30, 'day'),
    paymentTerms: '',
    pricelist: 'default_inr',
    items: [],
    additionalDiscount: 0,
    additionalDiscountType: 'percentage',
    shippingCharges: 0,
    termsAndConditions: '',
    customerNotes: '',
    internalNotes: '',
    salesperson: null,
    salespersonId: '',
    salesTeam: 'Sales',
    company: 'Tech 4 Logic Private Limited',
    fiscalPosition: 'Within Maharashtra',
    taxType: 'igst',
    createdAt: null,
  });

  const fetchData = useCallback(async () => {
    try {
      const [customersRes, productsRes, termsRes, salespersonsRes] = await Promise.all([
        customerAPI.getAllForDropdown(),
        productAPI.getAllForDropdown(),
        paymentTermAPI.getAllForDropdown(),
        userAPI.getSalespersons(),
      ]);
      setCustomers(customersRes.data);
      setProducts(productsRes.data);
      setPaymentTermsList(termsRes.data);
      setSalespersons(salespersonsRes.data);
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  }, []);

  const fetchQuotationsList = useCallback(async () => {
    try {
      const response = await quotationAPI.getAll({ limit: 1000 });
      const quotations = response.data.quotations || response.data || [];
      const ids = quotations.map(q => q._id);
      setQuotationIds(ids);
      setTotalRecords(ids.length);
    } catch (error) {
      console.error('Failed to fetch quotations list:', error);
    }
  }, []);

  const fetchQuotation = useCallback(async () => {
    try {
      setLoading(true);
      const response = await quotationAPI.getById(id);
      const quotation = response.data;
      
      // Use customerSnapshot if available, otherwise try to find customer in the customers list
      let customerData = quotation.customer;
      if (quotation.customerSnapshot) {
        // If customerSnapshot exists, use it but ensure it has _id
        customerData = {
          _id: quotation.customer,
          ...quotation.customerSnapshot
        };
      }
      
      setFormData({
        quotationNumber: quotation.quotationNumber || '',
        title: quotation.title || '',
        customer: customerData || null,
        quotationDate: quotation.quotationDate ? dayjs(quotation.quotationDate) : dayjs(),
        validUntil: quotation.validUntil ? dayjs(quotation.validUntil) : dayjs().add(30, 'day'),
        paymentTerms: quotation.paymentTerms || '',
        pricelist: quotation.pricelist || 'default_inr',
        items: (quotation.items || []).map(item => {
          // Helper to clean product names
          const cleanName = (name) => {
            if (!name) return '';
            return name
              .replace(/[\r\n]+/g, ' ')
              .replace(/\s+/g, ' ')
              .trim();
          };
          return {
            product: item.product?._id || item.product,
            productName: cleanName(item.productName || item.product?.name || ''),
            description: item.description || '',
            quantity: item.quantity || 1,
            unitPrice: item.unitPrice || 0,
            discount: item.discount || 0,
            discountType: item.discountType || 'percentage',
            taxRate: item.taxRate !== undefined ? item.taxRate : 18,
          };
        }),
        additionalDiscount: quotation.additionalDiscount || 0,
        additionalDiscountType: quotation.additionalDiscountType || 'percentage',
        shippingCharges: quotation.shippingCharges || 0,
        termsAndConditions: quotation.termsAndConditions || '',
        customerNotes: quotation.customerNotes || '',
        internalNotes: quotation.internalNotes || '',
        salesperson: quotation.salesPerson || null,
        salespersonId: quotation.salesPerson?._id || '',
        salesTeam: quotation.salesTeam || 'Sales',
        company: quotation.company || 'Tech 4 Logic Private Limited',
        fiscalPosition: quotation.fiscalPosition || 'Within Maharashtra',
        taxType: quotation.taxType || 'igst',
        createdAt: quotation.createdAt,
      });
      setStatus(quotation.status || 'draft');
    } catch (error) {
      toast.error('Failed to load quotation');
      navigate('/quotations');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
    fetchQuotationsList();
  }, [fetchData, fetchQuotationsList]);

  useEffect(() => {
    if (id) {
      fetchQuotation();
    }
  }, [id, fetchQuotation]);

  useEffect(() => {
    if (quotationIds.length > 0 && id) {
      const index = quotationIds.findIndex(qId => qId === id);
      if (index !== -1) {
        setCurrentIndex(index);
      }
    }
  }, [quotationIds, id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCustomerChange = (event, newValue) => {
    setFormData(prev => ({ ...prev, customer: newValue }));
  };

  const handleSalespersonChange = (event, newValue) => {
    setFormData(prev => ({ 
      ...prev, 
      salesperson: newValue,
      salespersonId: newValue?._id || '' 
    }));
  };

  // Handler for adding custom salesperson
  const handleAddCustomSalesperson = async () => {
    if (!newSalesperson.name.trim()) {
      toast.error('Please enter a name');
      return;
    }
    if (!newSalesperson.email.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    try {
      const response = await userAPI.create({
        name: newSalesperson.name,
        email: newSalesperson.email,
        phone: newSalesperson.phone || '',
        department: newSalesperson.department || '',
        role: newSalesperson.role || 'sales',
      });
      
      const createdSalesperson = response.data.user;
      setSalespersons([...salespersons, createdSalesperson]);
      setFormData(prev => ({ 
        ...prev, 
        salesperson: createdSalesperson,
        salespersonId: createdSalesperson._id 
      }));
      
      setNewSalesperson({
        name: '',
        email: '',
        phone: '',
        department: '',
        role: 'sales',
      });
      setSalespersonDialogOpen(false);
      toast.success('Salesperson created successfully');
    } catch (error) {
      console.error('Error creating salesperson:', error);
      toast.error(error.response?.data?.message || 'Failed to create salesperson');
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;

    if (field === 'product' && value) {
      const product = products.find(p => p._id === value);
      if (product) {
        newItems[index].productName = cleanProductName(product.name);
        newItems[index].taxRate = product.taxRate !== undefined ? product.taxRate : 18;
      }
    }

    setFormData(prev => ({ ...prev, items: newItems }));
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { ...initialLineItem }],
    }));
  };

  const removeLineItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const formatCurrency = (amount) => {
    return `₹ ${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const calculateLineTotal = (item) => {
    const qty = parseFloat(item.quantity) || 1;
    const price = parseFloat(item.unitPrice) || 0;
    const tax = item.taxRate !== undefined ? parseFloat(item.taxRate) : 18;
    const subtotal = qty * price;
    
    let discountAmount = 0;
    if (item.discount) {
      if (item.discountType === 'percentage') {
        discountAmount = (subtotal * parseFloat(item.discount)) / 100;
      } else {
        discountAmount = parseFloat(item.discount) || 0;
      }
    }
    
    const taxableAmount = subtotal - discountAmount;
    const taxAmount = (taxableAmount * tax) / 100;
    return taxableAmount + taxAmount;
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let totalItemDiscount = 0;
    let totalTax = 0;

    formData.items.forEach((item) => {
      const qty = parseFloat(item.quantity) || 1;
      const price = parseFloat(item.unitPrice) || 0;
      const tax = item.taxRate !== undefined ? parseFloat(item.taxRate) : 18;
      const lineSubtotal = qty * price;
      subtotal += lineSubtotal;
      
      let discountAmount = 0;
      if (item.discount) {
        if (item.discountType === 'percentage') {
          discountAmount = (lineSubtotal * parseFloat(item.discount)) / 100;
        } else {
          discountAmount = parseFloat(item.discount) || 0;
        }
      }
      totalItemDiscount += discountAmount;
      
      const taxableAmount = lineSubtotal - discountAmount;
      totalTax += (taxableAmount * tax) / 100;
    });

    let additionalDiscountAmount = 0;
    if (formData.additionalDiscountType === 'percentage') {
      additionalDiscountAmount = (subtotal * (parseFloat(formData.additionalDiscount) || 0)) / 100;
    } else {
      additionalDiscountAmount = parseFloat(formData.additionalDiscount) || 0;
    }
    
    const totalDiscount = totalItemDiscount + additionalDiscountAmount;
    const shippingCharges = parseFloat(formData.shippingCharges) || 0;
    const grandTotal = subtotal - totalDiscount + totalTax + shippingCharges;

    return { subtotal, totalDiscount, totalTax, grandTotal, shippingCharges };
  };

  const totals = calculateTotals();

  const getPaymentTermName = (termId) => {
    if (!termId) return 'Immediate';
    const term = paymentTermsList.find(t => t._id === termId);
    return term ? term.name : termId;
  };

  // Helper function to clean product names (remove newlines and extra spaces)
  const cleanProductName = (name) => {
    if (!name) return '';
    return name
      .replace(/[\r\n]+/g, ' ') // Replace newlines with space
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim(); // Remove leading/trailing spaces
  };

  const handleSubmit = async () => {
    if (!formData.customer) {
      toast.error('Please select a customer');
      return;
    }

    if (formData.items.length === 0) {
      toast.error('Please add at least one product');
      return;
    }

    try {
      setSaving(true);
      
      const title = formData.title || `Quotation for ${formData.customer.name || formData.customer.company || 'Customer'}`;
      
      const dataToSave = {
        title,
        customer: formData.customer._id || formData.customer,
        quotationDate: formData.quotationDate.toISOString(),
        validUntil: formData.validUntil.toISOString(),
        paymentTerms: formData.paymentTerms,
        pricelist: formData.pricelist,
        items: formData.items.map(item => ({
          product: item.product,
          productName: item.productName,
          description: item.description || '',
          quantity: parseFloat(item.quantity) || 1,
          unitPrice: parseFloat(item.unitPrice) || 0,
          discount: parseFloat(item.discount) || 0,
          discountType: item.discountType || 'percentage',
          taxRate: parseFloat(item.taxRate) || 18,
        })),
        additionalDiscount: parseFloat(formData.additionalDiscount) || 0,
        additionalDiscountType: formData.additionalDiscountType,
        shippingCharges: parseFloat(formData.shippingCharges) || 0,
        termsAndConditions: formData.termsAndConditions,
        customerNotes: formData.customerNotes,
        internalNotes: formData.internalNotes,
        status: status,
        salesperson: formData.salesperson?._id || formData.salespersonId || '',
      };

      await quotationAPI.update(id, dataToSave);
      toast.success('Quotation updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save quotation');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = () => {
    const quotationNumber = formData.quotationNumber || 'New';
    const customerName =
      formData.customer?.name || formData.customer?.company || 'Customer';
    const customerEmail = formData.customer?.email || '';

    setEmailData({
      recipients: customerEmail ? [{ name: customerName, email: customerEmail }] : [],
      subject: `Your Quotation #${quotationNumber} from Tech 4 Logic`,
      message: 'Thank you for your interest. Please find attached the quotation as per your requirements.',
      attachPdf: true,
    });
    setEmailDialogOpen(true);
  };

  const handleEmailSent = () => {
    setStatus('sent');
    fetchQuotation();
  };

  const handlePrint = async () => {
    setPrintLoading(true);
    try {
      const response = await quotationAPI.downloadPDF(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `quotation-${formData.quotationNumber || id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setPrintLoading(false);
    }
  };

  const handleConfirm = async () => {
    try {
      await quotationAPI.updateStatus(id, 'converted');
      setStatus('converted');
      toast.success('Quotation confirmed as Sales Order');
      fetchQuotation();
    } catch (error) {
      toast.error('Failed to confirm quotation');
    }
  };

  const handlePreview = async () => {
    try {
      setPreviewLoading(true);
      const response = await quotationAPI.downloadPDF(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewOpen(true);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const handleDuplicate = async () => {
    try {
      setDuplicating(true);
      const response = await quotationAPI.duplicate(id);
      const newQuotation = response.data.quotation;
      toast.success(`Quotation duplicated as ${newQuotation.quotationNumber}`);
      navigate(`/quotations/${newQuotation._id}`);
    } catch (error) {
      console.error('Duplicate error:', error);
      toast.error(error.response?.data?.message || 'Failed to duplicate quotation');
    } finally {
      setDuplicating(false);
    }
  };

  const handleCancel = async () => {
    try {
      await quotationAPI.updateStatus(id, 'rejected');
      toast.success('Quotation cancelled');
      navigate('/quotations');
    } catch (error) {
      toast.error('Failed to cancel quotation');
    }
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      setDeleting(true);
      await quotationAPI.delete(id);
      toast.success('Quotation deleted successfully');
      navigate('/quotations');
    } catch (error) {
      toast.error('Failed to delete quotation');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handlePreviousRecord = () => {
    if (currentIndex > 0) {
      navigate(`/quotations/${quotationIds[currentIndex - 1]}`);
    }
  };

  const handleNextRecord = () => {
    if (currentIndex < quotationIds.length - 1) {
      navigate(`/quotations/${quotationIds[currentIndex + 1]}`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ backgroundColor: '#f5f5f5', minHeight: 'calc(100vh - 64px)' }}>
        {/* Header */}
        <ViewPageHeader
          parentTitle="Quotations"
          currentTitle={formData.quotationNumber || 'Edit Quotation'}
          onParentClick={() => navigate('/quotations')}
          onNewClick={() => navigate('/quotations/new')}
          onClose={() => navigate('/quotations')}
          showNewButton
          pagination={{
            current: currentIndex + 1,
            total: totalRecords,
            onPrev: handlePreviousRecord,
            onNext: handleNextRecord,
          }}
        />

        {/* Action Bar */}
        <Box
          sx={{
            backgroundColor: 'white',
            borderBottom: '1px solid #dee2e6',
            px: { xs: 1, sm: 2 },
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleSend}
              sx={{
                backgroundColor: '#875A7B',
                textTransform: 'none',
                fontWeight: 400,
                fontSize: '13px',
                height: 30,
                px: 2,
                '&:hover': { backgroundColor: '#714B68' },
              }}
            >
              Send
            </Button>
            {/* Mobile: Show 3-dot menu */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <IconButton
                size="small"
                onClick={(e) => setActionMenuAnchor(e.currentTarget)}
                sx={{ border: '1px solid #dee2e6', borderRadius: 1, height: 30, width: 30 }}
              >
                <MoreVert sx={{ fontSize: 20 }} />
              </IconButton>
              <MuiMenu
                anchorEl={actionMenuAnchor}
                open={Boolean(actionMenuAnchor)}
                onClose={() => setActionMenuAnchor(null)}
                PaperProps={{ sx: { minWidth: 150, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' } }}
              >
                <MenuItem onClick={() => { setActionMenuAnchor(null); handlePrint(); }} disabled={printLoading}>
                  {printLoading
                    ? <CircularProgress size={18} sx={{ color: '#666', mr: 1 }} />
                    : <Print sx={{ fontSize: 18, color: '#666', mr: 1 }} />}
                  {printLoading ? 'Generating PDF...' : 'Print'}
                </MenuItem>
                <MenuItem onClick={() => { setActionMenuAnchor(null); handleConfirm(); }}>
                  <CheckCircle sx={{ fontSize: 18, color: '#666', mr: 1 }} />
                  Confirm
                </MenuItem>
                <MenuItem onClick={() => { setActionMenuAnchor(null); handlePreview(); }}>
                  <Visibility sx={{ fontSize: 18, color: '#666', mr: 1 }} />
                  Preview
                </MenuItem>
                <MenuItem onClick={() => { setActionMenuAnchor(null); handleDuplicate(); }} disabled={duplicating}>
                  <ContentCopy sx={{ fontSize: 18, color: '#666', mr: 1 }} />
                  {duplicating ? 'Duplicating...' : 'Duplicate'}
                </MenuItem>
                <MenuItem onClick={() => { setActionMenuAnchor(null); handleCancel(); }}>
                  <Close sx={{ fontSize: 18, color: '#666', mr: 1 }} />
                  Cancel
                </MenuItem>
                <MenuItem onClick={() => { setActionMenuAnchor(null); handleDelete(); }}>
                  <Delete sx={{ fontSize: 18, color: '#d32f2f', mr: 1 }} />
                  <Typography sx={{ color: '#d32f2f' }}>Delete</Typography>
                </MenuItem>
              </MuiMenu>
            </Box>
            {/* Desktop: Show individual buttons */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={printLoading ? <CircularProgress size={14} color="inherit" /> : <Print sx={{ fontSize: 16 }} />}
                onClick={handlePrint}
                disabled={printLoading}
                sx={{ textTransform: 'none', borderColor: '#dee2e6', color: '#333', fontWeight: 400, fontSize: '13px', height: 30, px: 1.5 }}
              >
                {printLoading ? 'Generating...' : 'Print'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CheckCircle sx={{ fontSize: 16 }} />}
                onClick={handleConfirm}
                sx={{ textTransform: 'none', borderColor: '#dee2e6', color: '#333', fontWeight: 400, fontSize: '13px', height: 30, px: 1.5 }}
              >
                Confirm
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={previewLoading ? <CircularProgress size={14} color="inherit" /> : <Visibility sx={{ fontSize: 16 }} />}
                onClick={handlePreview}
                disabled={previewLoading}
                sx={{ textTransform: 'none', borderColor: '#dee2e6', color: '#333', fontWeight: 400, fontSize: '13px', height: 30, px: 1.5 }}
              >
                {previewLoading ? 'Loading...' : 'Preview'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCopy sx={{ fontSize: 16 }} />}
                onClick={handleDuplicate}
                disabled={duplicating}
                sx={{ textTransform: 'none', borderColor: '#dee2e6', color: '#333', fontWeight: 400, fontSize: '13px', height: 30, px: 1.5 }}
              >
                {duplicating ? 'Duplicating...' : 'Duplicate'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancel}
                sx={{ textTransform: 'none', borderColor: '#dee2e6', color: '#333', fontWeight: 400, fontSize: '13px', height: 30, px: 1.5 }}
              >
                Cancel
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Delete sx={{ fontSize: 16 }} />}
                onClick={handleDelete}
                sx={{ textTransform: 'none', borderColor: '#d32f2f', color: '#d32f2f', fontWeight: 400, fontSize: '13px', height: 30, px: 1.5, '&:hover': { borderColor: '#b71c1c', backgroundColor: 'rgba(211, 47, 47, 0.04)' } }}
              >
                Delete
              </Button>
            </Box>
          </Box>

          {/* Status Progress Indicator (Arrow Steps - Odoo Style) */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', height: 30 }}>
            {/* Quotation Step */}
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                px: 2,
                backgroundColor: 'white',
                color: status === 'draft' ? '#017e84' : '#888',
                fontSize: '13px',
                fontWeight: status === 'draft' ? 500 : 400,
                border: status === 'draft' ? '1px solid #017e84' : '1px solid #d0d0d0',
                borderRight: 'none',
                borderTopLeftRadius: '3px',
                borderBottomLeftRadius: '3px',
                zIndex: 3,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  right: -12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 0,
                  height: 0,
                  borderTop: '15px solid transparent',
                  borderBottom: '15px solid transparent',
                  borderLeft: status === 'draft' ? '12px solid #017e84' : '12px solid #d0d0d0',
                  zIndex: 2,
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  right: -10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 0,
                  height: 0,
                  borderTop: '13px solid transparent',
                  borderBottom: '13px solid transparent',
                  borderLeft: '10px solid white',
                  zIndex: 3,
                },
              }}
            >
              Quotation
            </Box>
            {/* Quotation Sent Step */}
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                pl: 3,
                pr: 2,
                backgroundColor: 'white',
                color: status === 'sent' ? '#017e84' : '#888',
                fontSize: '13px',
                fontWeight: status === 'sent' ? 500 : 400,
                borderTop: status === 'sent' ? '1px solid #017e84' : '1px solid #d0d0d0',
                borderBottom: status === 'sent' ? '1px solid #017e84' : '1px solid #d0d0d0',
                zIndex: 2,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  right: -12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 0,
                  height: 0,
                  borderTop: '15px solid transparent',
                  borderBottom: '15px solid transparent',
                  borderLeft: status === 'sent' ? '12px solid #017e84' : '12px solid #d0d0d0',
                  zIndex: 2,
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  right: -10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 0,
                  height: 0,
                  borderTop: '13px solid transparent',
                  borderBottom: '13px solid transparent',
                  borderLeft: '10px solid white',
                  zIndex: 3,
                },
              }}
            >
              Quotation Sent
            </Box>
            {/* Sales Order Step */}
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                pl: 3,
                pr: 2,
                backgroundColor: 'white',
                color: status === 'converted' ? '#017e84' : '#888',
                fontSize: '13px',
                fontWeight: status === 'converted' ? 500 : 400,
                border: status === 'converted' ? '1px solid #017e84' : '1px solid #d0d0d0',
                borderLeft: 'none',
                borderTopRightRadius: '3px',
                borderBottomRightRadius: '3px',
                zIndex: 1,
              }}
            >
              Sales Order
            </Box>
          </Box>
        </Box>

        {/* Main Content */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, p: 2, gap: 2 }}>
          {/* Left - Quotation Details */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Paper sx={{ p: 3, border: '1px solid #dee2e6', boxShadow: 'none' }}>
              {/* Quotation Number - Editable */}
              <TextField
                fullWidth
                name="quotationNumber"
                value={formData.quotationNumber}
                onChange={handleChange}
                placeholder="Quotation Number"
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: { fontSize: '2rem', fontWeight: 400, color: '#333' },
                }}
                sx={{ mb: 3, borderBottom: '1px solid #dee2e6' }}
              />

              {/* Form Fields */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 4 }, mb: 3 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* Customer - Editable with Add New */}
                  <CustomerSelect
                    value={formData.customer}
                    onChange={(newValue) => setFormData(prev => ({ ...prev, customer: newValue }))}
                    customers={customers}
                    onCustomersChange={setCustomers}
                  />
                  {/* Customer Address Display */}
                  {formData.customer && (
                    <Box sx={{ ml: { sm: '140px' }, mb: 2 }}>
                      {formData.customer.address?.street && (
                        <Typography sx={{ fontSize: '13px', color: '#666' }}>
                          {formData.customer.address.street}
                        </Typography>
                      )}
                      {(formData.customer.address?.city || formData.customer.address?.zipCode) && (
                        <Typography sx={{ fontSize: '13px', color: '#666' }}>
                          {formData.customer.address.city} {formData.customer.address.zipCode}
                        </Typography>
                      )}
                      {formData.customer.gstNumber && (
                        <Typography sx={{ fontSize: '13px', color: '#666' }}>
                          GSTIN: {formData.customer.gstNumber}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* Expiration Date - Editable */}
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, mb: 2, alignItems: 'center' }}>
                    <Typography sx={{ width: { xs: 'auto', sm: 130 }, minWidth: { sm: 130 }, color: '#666', fontSize: '14px', mb: { xs: 0.5, sm: 0 } }}>
                      Expiration <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                    </Typography>
                    <DatePicker
                      value={formData.validUntil}
                      onChange={(newValue) => setFormData(prev => ({ ...prev, validUntil: newValue }))}
                      slotProps={{
                        textField: {
                          size: 'small',
                          variant: 'standard',
                          InputProps: { disableUnderline: true },
                          sx: { borderBottom: '1px solid #dee2e6', flex: 1 },
                        },
                      }}
                    />
                  </Box>
                  {/* Pricelist - Editable */}
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, mb: 2, alignItems: 'center' }}>
                    <Typography sx={{ width: { xs: 'auto', sm: 130 }, minWidth: { sm: 130 }, color: '#666', fontSize: '14px', mb: { xs: 0.5, sm: 0 } }}>
                      Pricelist <sup style={{ color: '#875A7B', cursor: 'help' }}>?</sup>
                    </Typography>
                    <TextField
                      select
                      name="pricelist"
                      value={formData.pricelist}
                      onChange={handleChange}
                      variant="standard"
                      size="small"
                      InputProps={{ disableUnderline: true }}
                      sx={{ flex: 1, borderBottom: '1px solid #dee2e6' }}
                    >
                      <MenuItem value="default_inr">Default (INR)</MenuItem>
                      <MenuItem value="wholesale">Wholesale</MenuItem>
                      <MenuItem value="retail">Retail</MenuItem>
                    </TextField>
                  </Box>
                  {/* Payment Terms - Editable with Add New */}
                  <PaymentTermSelect
                    value={formData.paymentTerms}
                    onChange={(newValue) => setFormData(prev => ({ ...prev, paymentTerms: newValue }))}
                    paymentTerms={paymentTermsList}
                    onPaymentTermsChange={setPaymentTermsList}
                  />
                </Box>
              </Box>

              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs
                  value={activeTab}
                  onChange={(e, newValue) => setActiveTab(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      minWidth: 'auto',
                      px: { xs: 1.5, sm: 2 },
                      color: '#666',
                      fontSize: '13px',
                    },
                    '& .Mui-selected': {
                      color: '#212529 !important',
                      fontWeight: 500,
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#875A7B',
                    },
                  }}
                >
                  <Tab label="Order Lines" />
                  <Tab label="Other Info" />
                  <Tab label="Notes" />
                </Tabs>
              </Box>

              {/* Order Lines Tab - Editable */}
              {activeTab === 0 && (
                <>
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 650 }}>
                      <TableHead>
                        <TableRow sx={{ '& th': { borderBottom: '2px solid #e0e0e0', py: 1, color: '#666', fontSize: '13px' } }}>
                          <TableCell sx={{ width: 50 }}>#</TableCell>
                          <TableCell sx={{ minWidth: 350 }}>Product</TableCell>
                          <TableCell sx={{ width: 100 }}>Quantity</TableCell>
                          <TableCell sx={{ width: 120 }}>Unit Price</TableCell>
                          <TableCell sx={{ width: 100 }}>Tax %</TableCell>
                          <TableCell sx={{ width: 120, textAlign: 'right' }}>Amount</TableCell>
                          <TableCell sx={{ width: 50 }}></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formData.items.map((item, index) => (
                          <TableRow key={index} sx={{ '& td': { borderBottom: '1px solid #f0f0f0', py: 1.5 } }}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {index + 1}
                                <DragIndicator sx={{ fontSize: 16, color: '#ccc', cursor: 'grab' }} />
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {item.productName && (
                                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#333', wordBreak: 'break-word' }}>
                                    {item.productName}
                                  </Typography>
                                )}
                                <ProductSelect
                                  value={item.product}
                                  onChange={(productId) => handleItemChange(index, 'product', productId)}
                                  products={products}
                                  onProductsChange={setProducts}
                                  onProductSelect={(product) => {
                                    const newItems = [...formData.items];
                                    newItems[index].productName = cleanProductName(product.name);
                                    newItems[index].unitPrice = product.unitPrice || 0;
                                    newItems[index].taxRate = product.taxRate !== undefined ? product.taxRate : 18;
                                    setFormData(prev => ({ ...prev, items: newItems }));
                                  }}
                                />
                                <TextField
                                  fullWidth
                                  value={item.description}
                                  onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                  placeholder="Description..."
                                  variant="standard"
                                  size="small"
                                  InputProps={{ disableUnderline: true, sx: { fontSize: '12px', color: '#666' } }}
                                  multiline
                                  maxRows={3}
                                />
                              </Box>
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                value={item.quantity}
                                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                variant="standard"
                                size="small"
                                InputProps={{ disableUnderline: true, inputProps: { min: 1 } }}
                                sx={{ width: 80, borderBottom: '1px solid #e0e0e0' }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                value={item.unitPrice}
                                onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                                variant="standard"
                                size="small"
                                InputProps={{ disableUnderline: true, inputProps: { min: 0 } }}
                                sx={{ width: 100, borderBottom: '1px solid #e0e0e0' }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                value={item.taxRate}
                                onChange={(e) => handleItemChange(index, 'taxRate', e.target.value)}
                                variant="standard"
                                size="small"
                                InputProps={{ disableUnderline: true, inputProps: { min: 0 } }}
                                sx={{ width: 60, borderBottom: '1px solid #e0e0e0' }}
                              />
                            </TableCell>
                            <TableCell sx={{ textAlign: 'right' }}>
                              <Typography sx={{ fontSize: '14px' }}>
                                {formatCurrency(calculateLineTotal(item))}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <IconButton size="small" onClick={() => removeLineItem(index)}>
                                <Delete sx={{ fontSize: 16, color: '#999' }} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Add Line Item Button */}
                  <Box sx={{ mt: 2 }}>
                    <Button
                      startIcon={<Add />}
                      onClick={addLineItem}
                      sx={{ textTransform: 'none', color: '#017e84' }}
                    >
                      Add a product
                    </Button>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Totals Section */}
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', gap: 4, mt: 3 }}>
                    {/* Left - Terms */}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#017e84', mb: 1.5 }}>
                        Terms & Conditions
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={6}
                        name="termsAndConditions"
                        value={formData.termsAndConditions}
                        onChange={handleChange}
                        placeholder="Enter terms and conditions..."
                        variant="outlined"
                        size="small"
                      />
                    </Box>

                    {/* Right - Totals */}
                    <Box sx={{ width: { xs: '100%', md: 280 }, textAlign: 'right' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography sx={{ color: '#666', fontSize: '13px' }}>Untaxed Amount:</Typography>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{formatCurrency(totals.subtotal)}</Typography>
                      </Box>
                      {totals.totalDiscount > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography sx={{ color: '#666', fontSize: '13px' }}>Discount:</Typography>
                          <Typography sx={{ fontSize: '13px', color: 'error.main' }}>-{formatCurrency(totals.totalDiscount)}</Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography sx={{ color: '#666', fontSize: '13px' }}>IGST:</Typography>
                        <Typography sx={{ fontSize: '13px' }}>{formatCurrency(totals.totalTax)}</Typography>
                      </Box>
                      {totals.shippingCharges > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography sx={{ color: '#666', fontSize: '13px' }}>Shipping:</Typography>
                          <Typography sx={{ fontSize: '13px' }}>+{formatCurrency(totals.shippingCharges)}</Typography>
                        </Box>
                      )}
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>Total:</Typography>
                        <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>{formatCurrency(totals.grandTotal)}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </>
              )}

              {/* Other Info Tab */}
              {activeTab === 1 && (
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, py: 2 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                      Sales
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#666', fontSize: '13px' }}>Salesperson</Typography>
                      <Autocomplete
                        size="small"
                        sx={{ flex: 1 }}
                        options={salespersons}
                        getOptionLabel={(option) => option?.name || ''}
                        value={formData.salesperson}
                        onChange={handleSalespersonChange}
                        isOptionEqualToValue={(option, value) => option?._id === value?._id}
                        renderOption={(props, option) => (
                          <li {...props} key={option._id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 24, height: 24, fontSize: '12px', bgcolor: '#00a09d' }}>
                                {option.name?.charAt(0)?.toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography sx={{ fontSize: '13px' }}>{option.name}</Typography>
                                <Typography sx={{ fontSize: '11px', color: '#666' }}>{option.email}</Typography>
                              </Box>
                            </Box>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select..."
                            variant="standard"
                            InputProps={{
                              ...params.InputProps,
                              disableUnderline: true,
                              endAdornment: (
                                <>
                                  {params.InputProps.endAdornment}
                                  <InputAdornment position="end">
                                    <Tooltip title="Add new salesperson">
                                      <IconButton 
                                        size="small" 
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSalespersonDialogOpen(true);
                                        }}
                                        sx={{ p: 0.5, color: '#00a09d' }}
                                      >
                                        <PersonAdd sx={{ fontSize: 18 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </InputAdornment>
                                </>
                              ),
                            }}
                            sx={{ borderBottom: '1px solid #dee2e6' }}
                          />
                        )}
                        noOptionsText={
                          <Box sx={{ textAlign: 'center', py: 1 }}>
                            <Typography sx={{ fontSize: '13px', color: '#666', mb: 1 }}>No salespersons found</Typography>
                            <Button
                              size="small"
                              startIcon={<PersonAdd />}
                              onClick={() => setSalespersonDialogOpen(true)}
                              sx={{ textTransform: 'none', color: '#00a09d' }}
                            >
                              Add new salesperson
                            </Button>
                          </Box>
                        }
                      />
                    </Box>
                    <Box sx={{ display: 'flex', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#666', fontSize: '13px' }}>Sales Team</Typography>
                      <TextField
                        name="salesTeam"
                        value={formData.salesTeam}
                        onChange={handleChange}
                        variant="standard"
                        size="small"
                        InputProps={{ disableUnderline: true }}
                        sx={{ flex: 1, borderBottom: '1px solid #dee2e6' }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#666', fontSize: '13px' }}>Company</Typography>
                      <TextField
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        variant="standard"
                        size="small"
                        InputProps={{ disableUnderline: true }}
                        sx={{ flex: 1, borderBottom: '1px solid #dee2e6' }}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                      Invoicing
                    </Typography>
                    <Box sx={{ display: 'flex', mb: 1.5 }}>
                      <Typography sx={{ width: 140, color: '#666', fontSize: '13px' }}>Fiscal Position</Typography>
                      <TextField
                        name="fiscalPosition"
                        value={formData.fiscalPosition}
                        onChange={handleChange}
                        variant="standard"
                        size="small"
                        InputProps={{ disableUnderline: true }}
                        sx={{ flex: 1, borderBottom: '1px solid #dee2e6' }}
                      />
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Notes Tab */}
              {activeTab === 2 && (
                <Box sx={{ py: 2 }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 2, color: '#666' }}>
                    Customer Notes
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    name="customerNotes"
                    value={formData.customerNotes}
                    onChange={handleChange}
                    placeholder="Notes visible to customer..."
                    variant="outlined"
                    size="small"
                    sx={{ mb: 3 }}
                  />
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 2, color: '#666' }}>
                    Internal Notes
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    name="internalNotes"
                    value={formData.internalNotes}
                    onChange={handleChange}
                    placeholder="Internal notes (not visible to customer)..."
                    variant="outlined"
                    size="small"
                  />
                </Box>
              )}

              {/* Save/Discard Buttons */}
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={saving || !formData.customer}
                  sx={{
                    backgroundColor: '#875A7B',
                    textTransform: 'none',
                    px: 3,
                    '&:hover': { backgroundColor: '#714B68' },
                    '&:disabled': { backgroundColor: '#d0d0d0', color: '#888' },
                  }}
                >
                  {saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Save'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/quotations')}
                  sx={{
                    borderColor: '#ccc',
                    color: '#666',
                    textTransform: 'none',
                    '&:hover': { borderColor: '#999' },
                  }}
                >
                  Discard
                </Button>
              </Box>
            </Paper>
          </Box>

          {/* Right - Activity Panel */}
          <Box sx={{ width: { xs: '100%', lg: 380 }, flexShrink: 0 }}>
            <ActivityPanel
              documentType="quotation"
              documentId={id}
              documentNumber={formData.quotationNumber}
              fetchActivities={async (docType, docId) => {
                try {
                  const response = await activityLogAPI.getByDocument(docType, docId);
                  return response.data;
                } catch (error) {
                  console.error('Error fetching activities:', error);
                  return { logs: [] };
                }
              }}
              onSendMessage={async (data) => {
                try {
                  await activityLogAPI.addMessage('quotation', id, data.content, formData.quotationNumber);
                  toast.success('Message sent');
                } catch (error) {
                  toast.error('Failed to send message');
                }
              }}
              onLogNote={async (data) => {
                try {
                  await activityLogAPI.addNote('quotation', id, data.content, formData.quotationNumber);
                  toast.success('Note logged');
                } catch (error) {
                  toast.error('Failed to log note');
                }
              }}
              onScheduleActivity={(data) => toast.info('Activity scheduled')}
              user={user}
              showFollowers
              followers={[]}
            />
          </Box>
        </Box>
      </Box>

      {/* Custom Salesperson Dialog */}
      <Dialog 
        open={salespersonDialogOpen} 
        onClose={() => {
          setSalespersonDialogOpen(false);
          setNewSalesperson({
            name: '', email: '', phone: '', department: '', role: 'sales',
          });
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '4px',
          }
        }}
      >
        {/* Header */}
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: '1px solid #e0e0e0',
          py: 1.5,
          px: 2,
          backgroundColor: '#f8f8f8',
        }}>
          <Typography sx={{ fontWeight: 500, fontSize: '16px', color: '#333' }}>
            Create Salesperson
          </Typography>
          <IconButton 
            size="small" 
            onClick={() => {
              setSalespersonDialogOpen(false);
              setNewSalesperson({
                name: '', email: '', phone: '', department: '', role: 'sales',
              });
            }}
            sx={{ color: '#666' }}
          >
            <Close sx={{ fontSize: '18px' }} />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {/* Avatar + Name */}
          <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
            {/* Avatar */}
            <Box sx={{ 
              width: 80, 
              height: 80, 
              backgroundColor: '#f5f0f0', 
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Person sx={{ fontSize: 40, color: '#00a09d' }} />
            </Box>
            
            {/* Name */}
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                variant="standard"
                value={newSalesperson.name}
                onChange={(e) => setNewSalesperson({ ...newSalesperson, name: e.target.value })}
                placeholder="Full Name"
                InputProps={{
                  disableUnderline: true,
                  sx: { 
                    fontSize: '24px', 
                    fontWeight: 400,
                    color: '#333',
                    borderBottom: '1px solid #e0e0e0',
                    pb: 0.5,
                  }
                }}
                sx={{ mb: 2 }}
              />
              
              {/* Email */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Email sx={{ fontSize: '16px', color: '#666' }} />
                <TextField
                  fullWidth
                  variant="standard"
                  value={newSalesperson.email}
                  onChange={(e) => setNewSalesperson({ ...newSalesperson, email: e.target.value })}
                  placeholder="Email"
                  InputProps={{
                    disableUnderline: true,
                    sx: { fontSize: '14px', color: '#666', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                  }}
                />
              </Box>
            </Box>
          </Box>
          
          {/* Other Fields */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Phone sx={{ fontSize: '16px', color: '#666' }} />
                <TextField
                  fullWidth
                  variant="standard"
                  value={newSalesperson.phone}
                  onChange={(e) => setNewSalesperson({ ...newSalesperson, phone: e.target.value })}
                  placeholder="Phone"
                  InputProps={{
                    disableUnderline: true,
                    sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Business sx={{ fontSize: '16px', color: '#666' }} />
                <TextField
                  fullWidth
                  variant="standard"
                  value={newSalesperson.department}
                  onChange={(e) => setNewSalesperson({ ...newSalesperson, department: e.target.value })}
                  placeholder="Department"
                  InputProps={{
                    disableUnderline: true,
                    sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', mb: 1 }}>
                <Typography sx={{ width: '80px', fontSize: '13px', color: '#333', pt: 1 }}>Role</Typography>
                <TextField
                  fullWidth
                  select
                  variant="standard"
                  value={newSalesperson.role}
                  onChange={(e) => setNewSalesperson({ ...newSalesperson, role: e.target.value })}
                  InputProps={{
                    disableUnderline: true,
                    sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                  }}
                >
                  <MenuItem value="sales">Sales</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </TextField>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        
        {/* Footer */}
        <DialogActions sx={{ 
          px: 2, 
          py: 1.5, 
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f8f8f8',
          justifyContent: 'flex-start',
        }}>
          <Button
            variant="contained"
            onClick={handleAddCustomSalesperson}
            sx={{
              backgroundColor: '#00a09d',
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 500,
              px: 2,
              '&:hover': { backgroundColor: '#008f8c' },
            }}
          >
            Save
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setSalespersonDialogOpen(false);
              setNewSalesperson({
                name: '', email: '', phone: '', department: '', role: 'sales',
              });
            }}
            sx={{
              backgroundColor: '#4a4a4a',
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 500,
              px: 2,
              '&:hover': { backgroundColor: '#333' },
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: '1px solid #e0e0e0',
          py: 1.5,
          px: 2,
          backgroundColor: '#f8f8f8',
        }}>
          <Typography sx={{ fontWeight: 500, fontSize: '16px', color: '#333' }}>
            Quotation Preview - {formData.quotationNumber}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={printLoading ? <CircularProgress size={14} color="inherit" /> : <Print sx={{ fontSize: 16 }} />}
              onClick={handlePrint}
              disabled={printLoading}
              sx={{ 
                textTransform: 'none', 
                borderColor: '#dee2e6', 
                color: '#333', 
                fontSize: '12px',
                height: 28,
              }}
            >
              {printLoading ? 'Generating...' : 'Download'}
            </Button>
            <IconButton 
              size="small" 
              onClick={handleClosePreview}
              sx={{ color: '#666' }}
            >
              <Close sx={{ fontSize: '18px' }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {previewUrl ? (
            <iframe
              src={previewUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                flex: 1,
              }}
              title="Quotation Preview"
            />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Quotation"
        message={`Are you sure you want to delete quotation "${formData.quotationNumber}"? This action cannot be undone.`}
        type="danger"
        loading={deleting}
      />

      {/* Email Send Dialog */}
      <SendQuotationDialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        quotationId={id}
        initialRecipients={emailData.recipients}
        initialSubject={emailData.subject}
        initialMessage={emailData.message}
        initialAttachPdf={emailData.attachPdf}
        pdfLabel={`Quotation - ${formData.quotationNumber || (id ? `S${id.slice(-5).toUpperCase()}` : 'New')}.pdf`}
        onSent={handleEmailSent}
      />
    </LocalizationProvider>
  );
};

export default QuotationView;
